// API Client for LoanFlow
// In production/Docker, use relative URL so nginx proxies to backend
// In development, VITE_API_URL can be set to direct backend URL
const API_BASE_URL = import.meta.env.VITE_API_URL ?? '';

// API Error class
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Request options type
interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
}

// Make an API request
async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {} } = options;

  const url = `${API_BASE_URL}${endpoint}`;

  // Build headers
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  };

  // Build request config - cookies handle auth automatically
  const config: RequestInit = {
    method,
    headers: requestHeaders,
    credentials: 'include',
  };

  // Add body for non-GET requests
  if (body && method !== 'GET') {
    config.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(url, config);

    // Parse response
    let data: unknown;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    // Handle errors
    if (!response.ok) {
      const errorMessage = typeof data === 'object' && data && 'message' in data
        ? (data as { message: string }).message
        : `Request failed with status ${response.status}`;
      throw new ApiError(response.status, errorMessage, data);
    }

    return data as T;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(0, error instanceof Error ? error.message : 'Network error');
  }
}

// API methods
export const api = {
  get: <T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(endpoint, { ...options, method: 'POST', body }),

  put: <T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(endpoint, { ...options, method: 'PUT', body }),

  patch: <T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(endpoint, { ...options, method: 'PATCH', body }),

  delete: <T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(endpoint, { ...options, method: 'DELETE' }),
};

// ============================================================================
// Auth API (custom lender profile endpoints - me, profile)
// Sign-up, sign-in, sign-out are handled by Better Auth client directly
// ============================================================================

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  companyName?: string;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  companyName: string | null;
  logoUrl?: string | null;
  accentColor?: string | null;
  customDomain?: string | null;
  customDomainVerified?: boolean;
  senderDisplayName?: string | null;
  senderEmail?: string | null;
  senderEmailVerified?: boolean;
  payoffWireInstructions?: string | null;
  isAdmin?: boolean;
  settings?: Record<string, unknown>;
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  companyName?: string | null;
  settings?: Record<string, unknown>;
}

export interface UpdateProfileResponse {
  message: string;
  user: User;
}

export const authApi = {
  me: () => api.get<{ user: User }>('/api/auth/me').then(res => res.user),

  updateProfile: (data: UpdateProfileRequest) =>
    api.put<UpdateProfileResponse>('/api/auth/profile', data),
};

export interface SenderEmailStatus {
  senderDisplayName: string | null;
  senderEmail: string | null;
  senderEmailVerified: boolean;
  verificationPending: boolean;
}

export const senderEmailApi = {
  getStatus: () => api.get<SenderEmailStatus>('/api/auth/sender-email-status'),

  sendVerification: (data: { email: string; displayName?: string }) =>
    api.post<{ message: string; email: string }>('/api/auth/verify-sender-email', data),
};

// ============================================================================
// Dashboard API
// ============================================================================

export interface Metric {
  id: string;
  label: string;
  value: number;
  navigateTo: string;
  trend?: {
    direction: 'up' | 'down' | 'neutral';
    value: number;
    label: string;
  };
}

export interface UpcomingItem {
  id: string;
  type: 'payment_due' | 'loan_maturing';
  loanId: string;
  loanName: string;
  borrowerName: string;
  date: string;
  amount: number;
  description: string;
}

export const dashboardApi = {
  getMetrics: () => api.get<Metric[]>('/api/dashboard/metrics'),
  getUpcoming: () => api.get<UpcomingItem[]>('/api/dashboard/upcoming'),
};

// ============================================================================
// Loans API
// ============================================================================

export type LoanStatus = 'request' | 'approved' | 'active' | 'late' | 'matured' | 'declined';

export interface Loan {
  id: string;
  name: string;
  status: LoanStatus;
  amount: number;
  interestRate: number | null;
  term: number | null;
  termUnit: 'months' | 'years' | null;
  originationDate: string | null;
  maturityDate: string | null;
  propertyAddress: string;
  propertyType: string;
  loanToValue: number | null;
  lateFeePercent: number | null;
  borrower: {
    id: string;
    name: string;
    email: string;
    phone: string;
    company: string | null;
  };
  investments: Investment[];
  payments: Payment[];
  milestones: Milestone[];
  activities: Activity[];
  crossCollateralProperties: CrossCollateralProperty[];
  notificationPreferences: NotificationPreferences;
}

export interface Investment {
  id: string;
  investorId: string;
  investorName: string;
  amount: number;
  percentage: number;
  interestRate: number | null;
  expectedReturn: number | null;
  payoutSchedule: 'monthly' | 'quarterly' | 'at_maturity';
}

export interface Payment {
  id: string;
  type: 'interest' | 'principal' | 'principal_and_interest' | 'points' | 'late_fee' | 'extension_fee';
  amount: number;
  dueDate: string;
  status: 'scheduled' | 'received' | 'overdue';
  receivedDate: string | null;
  receivedAmount: number | null;
  lateFeeAppliedAt: string | null;
  relatedPaymentId: string | null;
}

export interface Milestone {
  id: string;
  type: 'payment_due' | 'update_request' | 'maturity';
  title: string;
  date: string;
  description: string;
  completed: boolean;
}

export interface Activity {
  id: string;
  type: string;
  title: string;
  description: string;
  date: string;
  user: string;
}

export interface CrossCollateralProperty {
  id: string;
  address: string;
  propertyType: 'SFR' | 'Multi-Family' | 'Commercial' | 'Land' | 'Mixed-Use';
  estimatedValue: number | null;
  existingLiens: number | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationPreferences {
  paymentReminders: boolean;
  paymentReminderDays: number;
  updateRequests: boolean;
  updateFrequency: 'weekly' | 'biweekly' | 'monthly' | 'quarterly';
  maturityAlerts: boolean;
  maturityAlertDays: number;
}

export interface CreateLoanRequest {
  name: string;
  amount: number;
  interestRate?: number;
  term?: number;
  termUnit?: 'months' | 'years';
  propertyAddress: string;
  propertyType: string;
  loanToValue?: number;
  borrower: {
    name: string;
    email: string;
    phone?: string;
    company?: string;
  };
}

export const loansApi = {
  list: (params?: { status?: LoanStatus | 'all'; search?: string }) => {
    const query = new URLSearchParams();
    if (params?.status && params.status !== 'all') query.set('status', params.status);
    if (params?.search) query.set('search', params.search);
    return api.get<Loan[]>(`/api/loans?${query.toString()}`);
  },

  get: (id: string) => api.get<Loan>(`/api/loans/${id}`),

  create: (data: CreateLoanRequest) => api.post<Loan>('/api/loans', data),

  update: (id: string, data: Partial<CreateLoanRequest>) => api.put<Loan>(`/api/loans/${id}`, data),

  delete: (id: string) => api.delete<void>(`/api/loans/${id}`),

  updateStatus: (id: string, status: string, note: string) =>
    api.patch<{ message: string; loan: { id: string; name: string; status: string; previousStatus: string } }>(
      `/api/loans/${id}/status`,
      { status, note }
    ),

  approve: (id: string) => api.post<Loan>(`/api/loans/${id}/approve`),

  decline: (id: string) => api.post<Loan>(`/api/loans/${id}/decline`),

  fund: (id: string, data?: { firstPaymentDate?: string }) => api.post<Loan>(`/api/loans/${id}/fund`, data),

  updateNotificationPreferences: (id: string, prefs: NotificationPreferences) =>
    api.put<Loan>(`/api/loans/${id}/notification-preferences`, prefs),

  extend: (id: string, data: { newMaturityDate: string; extensionFeeAmount: number; note?: string }) =>
    api.post<{ message: string; newMaturityDate: string; extensionFeePayment: Payment; interestPaymentsGenerated: number }>(
      `/api/loans/${id}/extend`,
      data
    ),
};

// ============================================================================
// Investors API
// ============================================================================

export type InvestorStatus = 'active' | 'inactive';

export type W9Status = 'not_requested' | 'requested' | 'received';

export interface Investor {
  id: string;
  name: string;
  email: string;
  phone: string;
  company: string | null;
  status: InvestorStatus;
  joinedDate: string;
  notes: string;
  fundAvailability: {
    trackingMethod: 'commitment' | 'manual';
    commitmentAmount: number | null;
    currentlyInvested: number;
    availableBalance: number;
  };
  payoutPreferences: {
    schedule: 'monthly' | 'quarterly' | 'at_maturity';
    paymentMethod: 'ach' | 'wire' | 'check';
    bankName: string | null;
    accountType: 'checking' | 'savings' | null;
    accountLastFour: string | null;
    routingLastFour: string | null;
  };
  w9FileUrl: string | null;
  w9Status: W9Status;
  w9RequestedAt: string | null;
  w9UploadedAt: string | null;
}

export interface CreateInvestorRequest {
  name: string;
  email: string;
  phone?: string;
  company?: string;
  notes?: string;
  commitmentAmount?: number | null;
}

// Investor Earnings types
export interface InvestorEarningsLoan {
  loanId: string;
  loanName: string;
  loanStatus: string;
  propertyAddress: string | null;
  investmentAmount: number;
  investorPercentage: number;
  interestRate: number | null;
  interestEarned: number;
  principalReturned: number;
  upfrontFee: number;
  totalPayments: number;
  isConnector: boolean;
  payoutSchedule: string | null;
  originationDate: string | null;
  maturityDate: string | null;
}

export interface MonthlyBreakdown {
  month: number;
  monthName: string;
  interestEarned: number;
  principalReturned: number;
}

export interface InvestorEarnings {
  investor: {
    id: string;
    name: string;
    email: string;
    company: string | null;
  };
  year: number;
  summary: {
    totalInterestEarned: number;
    totalPrincipalReturned: number;
    totalUpfrontFees: number;
    totalEarnings: number;
    totalInvested: number;
    activeLoanCount: number;
    totalLoanCount: number;
  };
  loanEarnings: InvestorEarningsLoan[];
  monthlyBreakdown: MonthlyBreakdown[];
}

export interface Form1099IntData {
  taxYear: number;
  formType: string;
  generatedAt: string;
  payer: {
    name: string;
    address: string;
    email: string;
    tin: string;
  };
  recipient: {
    name: string;
    email: string;
    company: string | null;
    tin: string;
  };
  box1_interestIncome: number;
  totalInvested: number;
  properties: Array<{
    loanName: string;
    propertyAddress: string | null;
    loanAmount: number;
    investmentAmount: number;
    investorPercentage: number;
  }>;
  numberOfLoans: number;
  meetsFilingThreshold: boolean;
}

export interface EarningsSummary {
  year: number;
  totalInterestPaid: number;
  totalInvested: number;
  investorCount: number;
  investorsAboveThreshold: number;
  investors: Array<{
    id: string;
    name: string;
    email: string;
    company: string | null;
    status: string;
    totalInvested: number;
    interestEarned: number;
    loanCount: number;
    meetsFilingThreshold: boolean;
  }>;
}

export const investorsApi = {
  list: (params?: { status?: InvestorStatus | 'all'; search?: string }) => {
    const query = new URLSearchParams();
    if (params?.status && params.status !== 'all') query.set('status', params.status);
    if (params?.search) query.set('search', params.search);
    return api.get<Investor[]>(`/api/investors?${query.toString()}`);
  },

  get: (id: string) => api.get<Investor>(`/api/investors/${id}`),

  create: (data: CreateInvestorRequest) => api.post<Investor>('/api/investors', data),

  update: (id: string, data: Partial<CreateInvestorRequest>) =>
    api.put<Investor>(`/api/investors/${id}`, data),

  delete: (id: string) => api.delete<void>(`/api/investors/${id}`),

  toggleStatus: (id: string) => api.put<Investor>(`/api/investors/${id}/status`),

  getLoans: (id: string) => api.get<Loan[]>(`/api/investors/${id}/loans`),

  updatePayoutPreferences: (id: string, prefs: Partial<Investor['payoutPreferences']>) =>
    api.put<{ message: string; payoutPreferences: Investor['payoutPreferences'] }>(
      `/api/investors/${id}/payout-preferences`,
      prefs
    ),

  updateFundAvailability: (id: string, fundAvailability: Partial<Investor['fundAvailability']>) =>
    api.put<{ message: string; fundAvailability: Investor['fundAvailability'] }>(
      `/api/investors/${id}/fund-availability`,
      fundAvailability
    ),

  uploadW9: (id: string, file: File): Promise<{ message: string; investor: { id: string; name: string; w9FileUrl: string; w9Status: W9Status; w9UploadedAt: string } }> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64 = (reader.result as string).split(',')[1];
          const response = await api.post<{ message: string; investor: { id: string; name: string; w9FileUrl: string; w9Status: W9Status; w9UploadedAt: string } }>(
            `/api/investors/${id}/w9/upload`,
            {
              file: base64,
              fileName: file.name,
              mimeType: file.type,
            }
          );
          resolve(response);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  },

  requestW9: (id: string) =>
    api.post<{ message: string; investor: { id: string; name: string; email: string; w9Status: W9Status; w9RequestedAt: string } }>(
      `/api/investors/${id}/w9/request`
    ),

  deleteW9: (id: string) =>
    api.delete<{ message: string; investor: { id: string; name: string; w9Status: W9Status } }>(
      `/api/investors/${id}/w9`
    ),

  getEarnings: (id: string, year?: number) => {
    const query = new URLSearchParams();
    if (year) query.set('year', year.toString());
    return api.get<InvestorEarnings>(`/api/investors/${id}/earnings?${query.toString()}`);
  },

  get1099Int: (id: string, year?: number) => {
    const query = new URLSearchParams();
    if (year) query.set('year', year.toString());
    return api.get<Form1099IntData>(`/api/investors/${id}/1099-int?${query.toString()}`);
  },

  getEarningsSummary: (year?: number) => {
    const query = new URLSearchParams();
    if (year) query.set('year', year.toString());
    return api.get<EarningsSummary>(`/api/investors/earnings/summary?${query.toString()}`);
  },
};

// ============================================================================
// Notifications API
// ============================================================================

export type NotificationType = 'email' | 'sms' | 'in_app';
export type NotificationStatus = 'pending' | 'delivered' | 'failed' | 'read';

export interface Notification {
  id: string;
  loanId: string | null;
  loanName: string | null;
  type: NotificationType;
  category: string;
  title: string;
  description: string | null;
  status: NotificationStatus;
  isRead: boolean;
  recipientName: string | null;
  recipientEmail: string | null;
  messageContent: string | null;
  failureReason: string | null;
  sentAt: string;
}

export interface NotificationsResponse {
  notifications: Notification[];
  unreadCount: number;
  total: number;
  limit: number;
  offset: number;
}

export const notificationsApi = {
  list: (params?: {
    unread?: boolean;
    type?: NotificationType;
    limit?: number;
    offset?: number;
  }) => {
    const query = new URLSearchParams();
    if (params?.unread) query.set('unread', 'true');
    if (params?.type) query.set('type', params.type);
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());
    return api.get<NotificationsResponse>(`/api/notifications?${query.toString()}`);
  },

  get: (id: string) => api.get<Notification>(`/api/notifications/${id}`),

  markAsRead: (id: string) => api.patch<{ success: boolean }>(`/api/notifications/${id}/read`),

  markAllAsRead: () => api.post<{ success: boolean; count: number }>('/api/notifications/read-all'),

  delete: (id: string) => api.delete<{ success: boolean }>(`/api/notifications/${id}`),

  sendManual: (loanId: string, data: { title: string; message: string; recipientEmail?: string }) =>
    api.post<{ message: string; notification: { id: string; title: string; sentAt: string } }>(
      `/api/loans/${loanId}/send-notification`,
      data
    ),
};

// ============================================================================
// Funding Requests API
// ============================================================================

export interface FundingRequestFormField {
  fieldId: string;
  label: string;
  value: any;
}

export interface FundingRequestDetail {
  id: string;
  status: 'pending' | 'converted' | 'rejected';
  submittedAt: string;
  convertedLoanId: string | null;
  form: {
    id: string;
    slug: string;
    branding: {
      companyName: string;
      logoUrl: string;
      accentColor: string;
    };
  };
  formData: FundingRequestFormField[];
  rawFormData: Record<string, any>;
}

export const fundingRequestsApi = {
  get: (id: string) =>
    api.get<{ request: FundingRequestDetail }>(`/api/funding-requests/${id}`),

  convert: (id: string) =>
    api.post<{
      message: string;
      loan: {
        id: string;
        name: string;
        status: string;
        amount: number;
        borrower: { id: string; name: string; email: string };
      };
    }>(`/api/funding-requests/${id}/convert`),

  reject: (id: string, reason?: string) =>
    api.post<{ message: string; request: { id: string; status: string; rejectionReason: string | null } }>(
      `/api/funding-requests/${id}/reject`,
      { reason }
    ),
};

// ============================================================================
// Upload API
// ============================================================================

export interface UploadedFile {
  id: string;
  name: string;
  fileName: string;
  mimeType: string;
  size: number;
  url: string;
  uploadedAt: string;
}

export interface UploadResponse {
  success: boolean;
  file: UploadedFile;
}

export interface StorageStatus {
  enabled: boolean;
  maxFileSize: string;
  allowedTypes: string[];
}

export const uploadApi = {
  /**
   * Get storage service status
   */
  getStatus: () => api.get<StorageStatus>('/api/upload/status'),

  /**
   * Upload a file (base64 encoded)
   */
  upload: (file: File, folder?: string): Promise<UploadResponse> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          // Extract base64 data (remove data URL prefix)
          const base64 = (reader.result as string).split(',')[1];

          const response = await api.post<UploadResponse>('/api/upload', {
            file: base64,
            fileName: file.name,
            mimeType: file.type,
            folder,
          });

          resolve(response);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  },

  /**
   * Generate a signed upload URL for direct upload
   */
  getSignedUrl: (fileName: string, mimeType: string, folder?: string) =>
    api.post<{
      uploadUrl: string;
      fileUrl: string;
      fileName: string;
      expiresIn: string;
    }>('/api/upload/signed-url', { fileName, mimeType, folder }),

  /**
   * Delete a file
   */
  delete: (filePath: string) => {
    // Base64 encode the file path to handle slashes
    const encodedPath = btoa(filePath);
    return api.delete<{ success: boolean; message: string }>(`/api/upload/${encodedPath}`);
  },
};

// ============================================================================
// Support API
// ============================================================================

export const supportApi = {
  submitTicket: (data: {
    title: string;
    description: string;
    screenshot?: File;
  }): Promise<{ success: boolean }> => {
    if (!data.screenshot) {
      return api.post('/api/support/tickets', {
        title: data.title,
        description: data.description,
      });
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64 = (reader.result as string).split(',')[1];
          const result = await api.post<{ success: boolean }>('/api/support/tickets', {
            title: data.title,
            description: data.description,
            screenshot: base64,
            screenshotName: data.screenshot!.name,
            screenshotMimeType: data.screenshot!.type,
          });
          resolve(result);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read screenshot'));
      reader.readAsDataURL(data.screenshot!);
    });
  },
};

// ============================================================================
// Loan Files API
// ============================================================================

export interface LoanFile {
  id: string;
  fileName: string;
  originalName: string;
  mimeType: string;
  size: number;
  category: string | null;
  description: string | null;
  uploadedBy: string | null;
  createdAt: string;
  url: string;
}

export interface LoanFilesResponse {
  files: LoanFile[];
}

export interface UploadLoanFileResponse {
  success: boolean;
  file: LoanFile;
}

export const loanFilesApi = {
  /**
   * Get all files for a loan
   */
  list: (loanId: string) =>
    api.get<LoanFilesResponse>(`/api/loans/${loanId}/files`),

  /**
   * Upload a file to a loan
   */
  upload: (loanId: string, file: File, options?: { category?: string; description?: string }): Promise<UploadLoanFileResponse> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          // Extract base64 data (remove data URL prefix)
          const base64 = (reader.result as string).split(',')[1];

          const response = await api.post<UploadLoanFileResponse>(`/api/loans/${loanId}/files`, {
            file: base64,
            fileName: file.name,
            mimeType: file.type,
            category: options?.category,
            description: options?.description,
          });

          resolve(response);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  },

  /**
   * Delete a file from a loan
   */
  delete: (loanId: string, fileId: string) =>
    api.delete<{ success: boolean; message: string }>(`/api/loans/${loanId}/files/${fileId}`),
};

// ============================================================================
// Cross-Collateral API
// ============================================================================

export interface CreateCrossCollateralRequest {
  address: string;
  propertyType: string;
  estimatedValue?: number | null;
  existingLiens?: number | null;
  notes?: string;
}

export const crossCollateralApi = {
  create: (loanId: string, data: CreateCrossCollateralRequest) =>
    api.post<{ message: string; crossCollateralProperty: CrossCollateralProperty }>(
      `/api/loans/${loanId}/cross-collateral`, data
    ),

  update: (loanId: string, propertyId: string, data: Partial<CreateCrossCollateralRequest>) =>
    api.put<{ message: string; crossCollateralProperty: CrossCollateralProperty }>(
      `/api/loans/${loanId}/cross-collateral/${propertyId}`, data
    ),

  delete: (loanId: string, propertyId: string) =>
    api.delete<{ message: string }>(
      `/api/loans/${loanId}/cross-collateral/${propertyId}`
    ),
};

// ============================================================================
// Branding API
// ============================================================================

export interface BrandingData {
  logoUrl: string | null;
  accentColor: string;
  companyName: string | null;
  customDomain?: string | null;
  customDomainVerified?: boolean;
}

export interface BrandingResolveResponse {
  logoUrl: string | null;
  accentColor: string;
  companyName: string | null;
  lenderId: string | null;
}

export const brandingApi = {
  get: () => api.get<BrandingData>('/api/branding'),

  update: (data: { logoUrl?: string | null; accentColor?: string; companyName?: string | null }) =>
    api.put<{ message: string } & BrandingData>('/api/branding', data),

  resolve: (hostname: string) =>
    api.get<BrandingResolveResponse>(
      `/api/branding/resolve?hostname=${encodeURIComponent(hostname)}`
    ),
};

// ============================================================================
// Custom Domain API
// ============================================================================

export interface DnsInstruction {
  type: string;
  host: string;
  value: string;
}

export interface CustomDomainStatus {
  domain: string | null;
  verified: boolean;
  verifiedAt: string | null;
  lastCheck: string | null;
  dnsInstructions: DnsInstruction[];
}

export interface CustomDomainSetResponse {
  message: string;
  domain: string;
  dnsInstructions: DnsInstruction[];
}

export interface CustomDomainVerifyResponse {
  verified: boolean;
  method?: string | null;
  message?: string;
  error?: string;
  renderRegistered?: boolean;
}

// ============================================================================
// Billing API
// ============================================================================

export interface PlanLimits {
  activeLoans: number | null; // null = unlimited
  investors: number | null;
  intakeForms: number | null;
  customDomain: boolean;
  brandingCustomization: boolean;
}

export interface PlanUsage {
  activeLoans: number;
  investors: number;
  intakeForms: number;
}

export interface SubscriptionInfo {
  id: string;
  status: string;
  plan: string;
  periodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

export interface BillingStatus {
  plan: 'starter' | 'pro';
  limits: PlanLimits;
  usage: PlanUsage;
  subscription: SubscriptionInfo | null;
  availablePlans: Array<{
    name: string;
    limits: PlanLimits;
  }>;
}

export const billingApi = {
  getStatus: () => api.get<BillingStatus>('/api/billing/status'),
  createPortalSession: () => api.post<{ url: string }>('/api/billing/portal'),
};

export const customDomainApi = {
  status: () => api.get<CustomDomainStatus>('/api/custom-domain/status'),

  set: (domain: string) =>
    api.post<CustomDomainSetResponse>('/api/custom-domain', { domain }),

  verify: () =>
    api.post<CustomDomainVerifyResponse>('/api/custom-domain/verify'),

  remove: () =>
    api.delete<{ message: string }>('/api/custom-domain'),
};

// ============================================================================
// Borrowers API (W-9 management)
// ============================================================================

export const borrowersApi = {
  requestW9: (id: string) =>
    api.post<{ message: string; borrower: { id: string; name: string; email: string; w9Status: W9Status; w9RequestedAt: string } }>(
      `/api/borrowers/${id}/w9/request`
    ),

  uploadW9: (id: string, file: File): Promise<{ message: string; borrower: { id: string; name: string; w9FileUrl: string; w9Status: W9Status; w9UploadedAt: string } }> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64 = (reader.result as string).split(',')[1];
          const response = await api.post<{ message: string; borrower: { id: string; name: string; w9FileUrl: string; w9Status: W9Status; w9UploadedAt: string } }>(
            `/api/borrowers/${id}/w9/upload`,
            {
              file: base64,
              fileName: file.name,
              mimeType: file.type,
            }
          );
          resolve(response);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  },

  deleteW9: (id: string) =>
    api.delete<{ message: string; borrower: { id: string; name: string; w9Status: W9Status } }>(
      `/api/borrowers/${id}/w9`
    ),
};

// ============================================================================
// Borrower Reports API (1098 tax reporting)
// ============================================================================

export interface BorrowerReportsSummary {
  year: number;
  totalInterestReceived: number;
  activeBorrowerCount: number;
  totalLoanCount: number;
  borrowersAboveThreshold: number;
  borrowers: Array<{
    id: string;
    name: string;
    email: string;
    company: string | null;
    interestPaid: number;
    loanCount: number;
    meetsFilingThreshold: boolean;
  }>;
}

export interface BorrowerReportDetail {
  borrower: {
    id: string;
    name: string;
    email: string;
    company: string | null;
  };
  year: number;
  summary: {
    totalInterestPaid: number;
    totalPrincipalPaid: number;
    totalLoanCount: number;
  };
  loanDetails: Array<{
    loanId: string;
    loanName: string;
    loanStatus: string;
    propertyAddress: string | null;
    loanAmount: number;
    interestRate: number | null;
    interestPaid: number;
    principalPaid: number;
    totalPayments: number;
  }>;
  monthlyBreakdown: MonthlyBreakdown[];
}

export interface Form1098Data {
  taxYear: number;
  formType: string;
  generatedAt: string;
  recipient: {
    name: string;
    email: string;
    company: string | null;
  };
  lender: {
    name: string;
    email: string;
  };
  box1_mortgageInterest: number;
  totalLoanBalance: number;
  properties: Array<{
    loanName: string;
    propertyAddress: string | null;
    loanAmount: number;
    interestPaid: number;
  }>;
  numberOfLoans: number;
  meetsFilingThreshold: boolean;
}

export const borrowerReportsApi = {
  getSummary: (year?: number) => {
    const query = new URLSearchParams();
    if (year) query.set('year', year.toString());
    return api.get<BorrowerReportsSummary>(`/api/borrower-reports/summary?${query.toString()}`);
  },

  getDetail: (id: string, year?: number) => {
    const query = new URLSearchParams();
    if (year) query.set('year', year.toString());
    return api.get<BorrowerReportDetail>(`/api/borrower-reports/${id}?${query.toString()}`);
  },

  get1098: (id: string, year?: number) => {
    const query = new URLSearchParams();
    if (year) query.set('year', year.toString());
    return api.get<Form1098Data>(`/api/borrower-reports/${id}/1098?${query.toString()}`);
  },
};

// ============================================================================
// Integrations API (GoHighLevel, etc.)
// ============================================================================

export interface GHLIntegrationStatus {
  connected: boolean;
  locationId?: string;
  tokenMasked?: string;
  borrowerTag?: string;
  investorTag?: string;
  connectedAt?: string;
  lastSyncError?: { at: string; message: string };
}

export interface GHLIntegrationSaveRequest {
  locationId: string;
  privateIntegrationToken?: string;
  borrowerTag: string;
  investorTag: string;
}

export const integrationsApi = {
  getGHL: () => api.get<GHLIntegrationStatus>('/api/integrations/ghl'),

  saveGHL: (body: GHLIntegrationSaveRequest) =>
    api.put<GHLIntegrationStatus>('/api/integrations/ghl', body),

  testGHL: () => api.post<{ ok: boolean; error?: string }>('/api/integrations/ghl/test'),

  disconnectGHL: () => api.delete<{ connected: false }>('/api/integrations/ghl'),
};

// ============================================================================
// Payoff Quotes API
// ============================================================================

export interface PayoffLineItem {
  type: 'principal' | 'accrued_interest' | 'past_due_interest' | 'late_fee' | 'extension_fee' | 'custom';
  label: string;
  amount: number;
  sourcePaymentId?: string;
}

export interface PayoffQuote {
  id: string;
  loanId: string;
  lenderId: string;
  quoteDate: string;
  payoffDate: string;
  interestPaidThroughDate: string;
  perDiemRate: number;
  lineItems: PayoffLineItem[];
  totalAmount: number;
  notes: string | null;
  pdfFileId: string | null;
  appliedAt: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface PayoffPreviewResponse {
  interestPaidThroughDate: string;
  perDiemRate: number;
  lineItems: PayoffLineItem[];
  totalAmount: number;
  wireInstructions: string | null;
}

export interface PayoffGenerateResponse {
  quote: PayoffQuote;
  pdfUrl: string | null;
  pdfBuffer?: string;
}

export const payoffQuotesApi = {
  list: (loanId: string) =>
    api.get<{ quotes: PayoffQuote[] }>(`/api/loans/${loanId}/payoff-quotes`),

  preview: (loanId: string, payoffDate: string) =>
    api.post<PayoffPreviewResponse>(`/api/loans/${loanId}/payoff-quotes/preview`, { payoffDate }),

  generate: (loanId: string, data: {
    payoffDate: string;
    interestPaidThroughDate: string;
    perDiemRate: number;
    lineItems: PayoffLineItem[];
    notes?: string;
    wireInstructions?: string;
  }) =>
    api.post<PayoffGenerateResponse>(`/api/loans/${loanId}/payoff-quotes`, data),

  update: (loanId: string, quoteId: string, data: {
    payoffDate: string;
    interestPaidThroughDate: string;
    perDiemRate: number;
    lineItems: PayoffLineItem[];
    notes?: string;
    wireInstructions?: string;
  }) =>
    api.patch<PayoffGenerateResponse>(`/api/loans/${loanId}/payoff-quotes/${quoteId}`, data),

  remove: (loanId: string, quoteId: string) =>
    api.delete<{ success: true }>(`/api/loans/${loanId}/payoff-quotes/${quoteId}`),

  getPdfUrl: (loanId: string, quoteId: string) =>
    `/api/loans/${loanId}/payoff-quotes/${quoteId}/pdf`,

  apply: (loanId: string, quoteId: string) =>
    api.post<{ quote: PayoffQuote }>(`/api/loans/${loanId}/payoff-quotes/${quoteId}/apply`),
};

export default api;
